import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusBadge,
} from '@/components/ui';
import { labelOf } from '@/lib/enumLabels';
import type { ManualOrderCreateResponse, Money } from '../types/manualOrder';

interface ManualOrderResultModalProps {
  result: ManualOrderCreateResponse | null;
  onClose: () => void;
}

const money = (m: Money | undefined, locale: string): string =>
  m ? `${m.amount.toLocaleString(locale)} ${m.currencyCode}` : '-';

/** ID55 수기주문 등록 성공 결과 모달. 주문번호·금액·결제 상태를 요약 표기한다. */
export const ManualOrderResultModal = ({ result, onClose }: ManualOrderResultModalProps) => {
  const { t, i18n } = useTranslation('manualOrder');
  const locale = i18n.language;

  // BE 응답 paymentMethod 는 요청 enum(BANK_TRANSFER/PAID)이 아니라 저장 어휘
  // VIRTUAL_ACCOUNT / MANUAL_PAID 로 변환되어 온다(be-o 계약). 정본 PAYMENT_METHOD_LABELS 엔
  // MANUAL_PAID 가 없으므로 수기주문 전용 로케일로 라벨화한다(raw 코드 노출 방지).
  const resultPaymentMethodLabels: Readonly<Record<string, string>> = {
    VIRTUAL_ACCOUNT: t('manualOrder.resultPaymentMethod.VIRTUAL_ACCOUNT'),
    MANUAL_PAID: t('manualOrder.resultPaymentMethod.MANUAL_PAID'),
  };

  return (
    <Dialog
      open={result !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('manualOrder.result.title')}</DialogTitle>
          <DialogDescription>{t('manualOrder.result.description')}</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col gap-2 py-1 text-[13px]">
            <Row label={t('manualOrder.result.orderNo')} value={result.orderNo} mono />
            <Row
              label={t('manualOrder.result.paymentMethod')}
              value={labelOf(resultPaymentMethodLabels, result.paymentMethod)}
            />
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--content-muted)' }}>{t('manualOrder.result.orderStatus')}</span>
              {/* displayStatus 는 BE 가 항상 도출해 공급(be-o 계약, 널 불가) → orderStatus(raw enum) 폴백 제거. */}
              <StatusBadge label={result.displayStatus} variant="info" size="sm" />
            </div>
            <div className="my-1 border-t" style={{ borderColor: 'var(--divider-soft)' }} />
            <Row label={t('manualOrder.result.itemAmount')} value={money(result.itemAmount, locale)} />
            <Row label={t('manualOrder.result.shippingFee')} value={money(result.shippingFee, locale)} />
            <Row label={t('manualOrder.result.discount')} value={money(result.discountAmount, locale)} />
            <div className="flex items-center justify-between font-semibold">
              <span style={{ color: 'var(--content-strong)' }}>{t('manualOrder.result.payable')}</span>
              <span className="tabular-nums" style={{ color: 'var(--content-strong)' }}>
                {money(result.payableAmount, locale)}
              </span>
            </div>
            {result.paymentDueAt ? (
              <Row label={t('manualOrder.result.paymentDue')} value={new Date(result.paymentDueAt).toLocaleString(locale)} />
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button size="sm" onClick={onClose} data-testid="manual-order-result-close">
            {t('manualOrder.result.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-center justify-between">
    <span style={{ color: 'var(--content-muted)' }}>{label}</span>
    <span
      className={mono ? 'tabular-nums' : undefined}
      style={{ color: 'var(--content-default)' }}
    >
      {value}
    </span>
  </div>
);
